import { Injectable, inject, signal } from '@angular/core';

import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { finalize } from 'rxjs/operators';
import { ApiLiv } from 'src/app/core/services/api/api-liv.service';
import { catchRemoteError } from 'src/app/shared/rxjs/custom-operators';

import { DEFAULT_EXTERNAL_LINK_TRACKING_PARAMS } from '../../external-link-cleaner/external-link-cleaner-options';
import { removeTrackingParams } from '../../external-link-cleaner/handlers/remove-tracking-params';
import {
  AUTHENTICATED_EXTERNAL_LINK_CONFIG,
  AuthenticatedExternalLinkRoute
} from '../authenticated-external-link-options';
import { findAuthenticatedExternalLinkRoute } from '../handlers/authenticated-external-link-click-handler';
import { AuthenticatedExternalLinkResponse } from '../resolvers/authenticated-external-link-url.resolvers';

export interface AuthenticatedExternalLinkLoadingContext {
  trackingId?: string;
  href: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticatedExternalLinkService {
  private readonly config = inject(AUTHENTICATED_EXTERNAL_LINK_CONFIG);
  private readonly apiLiv = inject(ApiLiv);
  private readonly toast = inject(ToastController);
  private readonly i18n = inject(I18nService);
  private readonly loadingContextState =
    signal<AuthenticatedExternalLinkLoadingContext | null>(null);

  private readonly catchOpenRequestError =
    catchRemoteError<AuthenticatedExternalLinkResponse>(
      (remoteError) => {
        this.handleOpenFailure({
          reason: 'request_failed',
          details: remoteError,
          message: remoteError.message
        });
      },
      {
        defaultMessageKey: 'items_menu.authenticated_external_link_error'
      }
    );

  readonly loadingContext = this.loadingContextState.asReadonly();

  isLoadingFor(
    context: Partial<AuthenticatedExternalLinkLoadingContext>
  ): boolean {
    const loadingContext = this.loadingContextState();

    if (!loadingContext) {
      return false;
    }

    if (context.trackingId && loadingContext.trackingId) {
      return loadingContext.trackingId === context.trackingId;
    }

    if (context.href) {
      return (
        this.normalizeHref(loadingContext.href) ===
        this.normalizeHref(context.href)
      );
    }

    return false;
  }

  openById(routeId: string, sourceHref: string, trackingId?: string): void {
    const route = this.config.routes.find(({ id }) => id === routeId);

    if (!route) {
      return;
    }

    this.openRoute(route, sourceHref, trackingId);
  }

  openForLink(href: string, trackingId?: string): boolean {
    const route = findAuthenticatedExternalLinkRoute(href, this.config.routes);

    if (!route) {
      return false;
    }

    this.openRoute(route, href, trackingId);

    return true;
  }

  openRoute(
    route: AuthenticatedExternalLinkRoute,
    sourceHref: string,
    trackingId?: string
  ): void {
    const cleanHref = this.stripTrackingParams(sourceHref);

    if (this.isLoadingFor({ trackingId, href: cleanHref })) {
      return;
    }

    this.loadingContextState.set({
      trackingId,
      href: cleanHref
    });

    this.apiLiv
      .get<AuthenticatedExternalLinkResponse>(route.urlEndpoint)
      .pipe(
        this.catchOpenRequestError,
        finalize(() => this.loadingContextState.set(null))
      )
      .subscribe((response) => {
        const url = route.resolveUrl({ response, sourceHref: cleanHref });

        if (!url) {
          this.handleOpenFailure({
            reason: 'invalid_response',
            details: {
              routeId: route.id,
              urlEndpoint: route.urlEndpoint,
              sourceHref: cleanHref,
              response
            }
          });
          return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
      });
  }

  private handleOpenFailure({
    reason,
    details,
    message
  }: {
    reason: 'request_failed' | 'invalid_response';
    details: unknown;
    message?: string;
  }): void {
    const loadingContext = this.loadingContextState();

    console.warn('[AuthenticatedExternalLink] failed to open link', {
      reason,
      sourceHref: loadingContext?.href,
      trackingId: loadingContext?.trackingId,
      details
    });

    this.toast.danger({
      message:
        message ??
        this.i18n.instant('items_menu.authenticated_external_link_error')
    });
  }

  /**
   * GA's cross-domain linker rewrites the anchor `href` on click, so the
   * clicked URL reaches us already carrying `_gl`/`_ga*` params. The target
   * site rejects them, so they are dropped before the token is appended.
   */
  private stripTrackingParams(href: string): string {
    try {
      return removeTrackingParams(href, DEFAULT_EXTERNAL_LINK_TRACKING_PARAMS);
    } catch {
      return href;
    }
  }

  private normalizeHref(href: string): string {
    try {
      const url = new URL(href);

      return `${url.origin}${url.pathname.replace(/\/$/, '')}${url.search}`;
    } catch {
      return href;
    }
  }
}
